import { useState } from "react";

// Pantalla de información: cómo opera cada estrategia que se puede crear desde la web.
// Los valores salen de server/bot/dca_scalp.py, low_volatility.py y short.py; si cambian allá, cambiar acá.

const C = {
  price: '#32325d', buy: '#2dce89', sell: '#fb6340', target: '#11cdef', stop: '#f5365c',
  ema: '#8965e0', muted: '#8898aa', zone: 'rgba(17,205,239,0.10)', zoneRed: 'rgba(251,99,64,0.10)',
};

const card = { background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '18px 20px', marginBottom: 20 };
const title = { fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' };

// Gráfico de ejemplo: línea de precio, niveles horizontales, marcas de compra/venta y zonas sombreadas.
const Chart = ({ points, levels = [], marks = [], zones = [], curve, minP, maxP, xLabels = [] }) => {
  const W = 640, H = 270, L = 12, R = 150, T = 14, B = 26;
  const ts = points.map(p => p[0]);
  const t0 = Math.min(...ts), t1 = Math.max(...ts);
  const x = t => L + (t - t0) / (t1 - t0) * (W - L - R);
  const y = p => T + (maxP - p) / (maxP - minP) * (H - T - B);
  const path = pts => pts.map((p, i) => `${i ? 'L' : 'M'}${x(p[0]).toFixed(1)},${y(p[1]).toFixed(1)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img">
      {zones.map((z, i) => (
        <g key={`z${i}`}>
          <rect x={x(z.from)} y={T} width={x(z.to) - x(z.from)} height={H - T - B} fill={z.color || C.zone} />
          <text x={(x(z.from) + x(z.to)) / 2} y={T + 14} textAnchor="middle" fontSize="11" fill={C.muted}>{z.label}</text>
        </g>
      ))}
      {levels.map((lv, i) => (
        <g key={`l${i}`}>
          <line x1={L} x2={W - R + 6} y1={y(lv.price)} y2={y(lv.price)} stroke={lv.color} strokeWidth="1.3"
                strokeDasharray={lv.solid ? '' : '5 4'} opacity="0.9" />
          <text x={W - R + 10} y={y(lv.price) + 4 + (lv.dy || 0)} fontSize="11.5" fontWeight="700" fill={lv.color}>{lv.label}</text>
        </g>
      ))}
      {curve && <path d={path(curve.points)} fill="none" stroke={curve.color} strokeWidth="2" opacity="0.8" />}
      <path d={path(points)} fill="none" stroke={C.price} strokeWidth="2.2" strokeLinejoin="round" />
      {marks.map((m, i) => (
        <g key={`m${i}`}>
          <circle cx={x(m.t)} cy={y(m.price)} r="7" fill={m.color} stroke="white" strokeWidth="2" />
          <text x={x(m.t)} y={y(m.price) + 4} textAnchor="middle" fontSize="9" fontWeight="800" fill="white">{m.n}</text>
          {m.label && (
            <text x={x(m.t) + (m.dx || 0)} y={y(m.price) + (m.dy || -13)} textAnchor="middle" fontSize="11" fontWeight="700" fill={m.color}>{m.label}</text>
          )}
        </g>
      ))}
      {xLabels.map((xl, i) => (
        <text key={`x${i}`} x={x(xl.t)} y={H - 6} textAnchor={xl.t === t0 ? "start" : "middle"} fontSize="10.5" fill={C.muted}>{xl.label}</text>
      ))}
    </svg>
  );
};

const Legend = ({ items }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: '#525f7f', marginTop: 6 }}>
    {items.map(([color, text]) => (
      <span key={text} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 12, height: 12, borderRadius: 6, background: color, display: 'inline-block' }} />{text}
      </span>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol style={{ paddingLeft: 20, margin: 0, color: '#525f7f', fontSize: 14, lineHeight: 1.6 }}>
    {items.map((s, i) => <li key={i} style={{ marginBottom: 4 }}>{s}</li>)}
  </ol>
);

const Rules = ({ rows }) => (
  <table style={{ width: '100%', fontSize: 13.5, borderCollapse: 'collapse' }}>
    <tbody>
      {rows.map(([k, v]) => (
        <tr key={k} style={{ borderBottom: '1px solid #f0f1f5' }}>
          <td style={{ padding: '7px 8px 7px 0', color: C.muted, fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{k}</td>
          <td style={{ padding: '7px 0', color: C.price }}>{v}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Note = ({ children }) => (
  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>{children}</div>
);

const Section = ({ name, tag, tagColor, summary, chart, legend, steps, rules, example, history, extra }) => (
  <div style={card}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
      <h2 style={{ margin: 0, fontSize: 22, color: C.price }}>{name}</h2>
      <span style={{ fontSize: 11, fontWeight: 800, color: 'white', background: tagColor, borderRadius: 10, padding: '3px 10px' }}>{tag}</span>
    </div>
    <p style={{ color: '#525f7f', fontSize: 15, marginBottom: 14 }}>{summary}</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
      <div style={{ flex: '1 1 420px', minWidth: 0 }}>
        <div style={title}>Ejemplo en el gráfico</div>
        {chart}
        <Legend items={legend} />
      </div>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>
        <div style={title}>Paso a paso</div>
        <Steps items={steps} />
      </div>
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, marginTop: 16 }}>
      <div style={{ flex: '1 1 420px', minWidth: 0 }}>
        <div style={title}>Reglas</div>
        <Rules rows={rules} />
      </div>
      <div style={{ flex: '1 1 300px', minWidth: 0 }}>
        <div style={title}>Con un bot de 150 USDT</div>
        <Rules rows={example} />
        {history && <Note>{history}</Note>}
      </div>
    </div>
    {extra && extra.map(b => (
      <div key={b.title} style={{ marginTop: 16, background: '#f6f4fe', borderLeft: `4px solid ${C.ema}`, borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ ...title, color: C.ema }}>{b.title}</div>
        {b.text.map((t, i) => <p key={i} style={{ fontSize: 14, color: '#525f7f', margin: i ? '6px 0 0' : 0, lineHeight: 1.55 }}>{t}</p>)}
      </div>
    ))}
  </div>
);

// ─── DCA Scalps ──────────────────────────────────────────────────────────────
const dcaScalps = (
  <Chart
    minP={89} maxP={101.5}
    points={[[0, 100], [3, 100.2], [6, 99.8], [9, 100.1], [10, 99], [11, 97.4], [12, 96], [14, 94.8], [16, 95.6],
             [18, 94.2], [20, 93.1], [22, 92.1], [24, 91.2], [26, 90.2], [27, 90.6], [29, 92.1], [31, 93.4], [33, 94.5], [35, 94.1]]}
    zones={[{ from: 9, to: 12, label: '15 min: cae 4%' }]}
    levels={[
      { price: 96, label: 'Compra 1 · 96.00', color: C.buy },
      { price: 94.44, label: 'Objetivo +1.5%', color: C.target, solid: true },
      { price: 93.12, label: 'Compra 2 · 93.12 (−3%)', color: C.buy },
      { price: 90.24, label: 'Compra 3 · 90.24 (−6%)', color: C.buy },
    ]}
    marks={[
      { t: 12, price: 96, n: 1, color: C.buy, label: 'compra', dx: 22, dy: -10 },
      { t: 20, price: 93.12, n: 2, color: C.buy, label: 'compra', dx: 20, dy: -10 },
      { t: 26, price: 90.24, n: 3, color: C.buy, label: 'compra', dy: 20 },
      { t: 33, price: 94.44, n: '$', color: C.target, label: 'vende todo', dy: -13 },
    ]}
    xLabels={[{ t: 0, label: '10:00' }, { t: 12, label: '10:15' }, { t: 20, label: '11:30' }, { t: 26, label: '12:30' }, { t: 33, label: '14:00' }]}
  />
);

// ─── DCA Dual (modo volatilidad baja) ────────────────────────────────────────
const dualCalm = (
  <Chart
    minP={95} maxP={101.2}
    curve={{ color: C.ema, points: [[0, 96.2], [35, 96.9]] }}
    points={[[0, 100], [3, 100.3], [6, 99.9], [9, 100.2], [10, 99.4], [11, 98.6], [12, 98], [14, 97.5], [16, 97.9],
             [19, 97.3], [21, 97.6], [24, 98.3], [27, 98.7], [29, 99.1], [31, 99.6], [33, 99.2], [35, 99.4]]}
    zones={[{ from: 9, to: 12, label: '15 min: cae 2%' }]}
    levels={[
      { price: 98, label: 'Compra 1 · 98.00', color: C.buy },
      { price: 96.53, label: 'Compra 2 · 96.53 (−1.5%)', color: C.buy },
      { price: 99.57, label: 'Objetivo +1.6%', color: C.target, solid: true },
    ]}
    marks={[
      { t: 12, price: 98, n: 1, color: C.buy, label: 'compra', dx: 22, dy: -10 },
      { t: 31, price: 99.57, n: '$', color: C.target, label: 'vende todo', dy: -13 },
    ]}
    xLabels={[{ t: 0, label: '22:00' }, { t: 12, label: '22:15' }, { t: 31, label: '02:30' }]}
  />
);

// ─── Short ───────────────────────────────────────────────────────────────────
const shortChart = (
  <Chart
    minP={99} maxP={112}
    points={[[0, 100], [3, 100.6], [6, 101.8], [9, 103.2], [12, 104.6], [15, 106], [17, 107], [19, 108.4], [21, 109.6],
             [23, 110.4], [25, 109.4], [27, 108.6], [29, 107.8], [31, 107.4], [33, 107.9], [35, 107.2]]}
    zones={[{ from: 0, to: 17, label: '4 horas: sube 7%', color: C.zoneRed }]}
    levels={[
      { price: 110.21, label: 'Venta 2 · 110.21 (+3%)', color: C.sell },
      { price: 107, label: 'Venta 1 · 107.00', color: C.sell, dy: 6 },
      { price: 107.5, label: 'Recompra −1%', color: C.target, solid: true, dy: -5 },
    ]}
    marks={[
      { t: 17, price: 107, n: 1, color: C.sell, label: 'vende', dx: -22, dy: -10 },
      { t: 23, price: 110.21, n: 2, color: C.sell, label: 'vende', dy: -13 },
      { t: 31, price: 107.5, n: '$', color: C.target, label: 'recompra todo', dy: 20 },
    ]}
    xLabels={[{ t: 0, label: '08:00' }, { t: 17, label: '12:00' }, { t: 23, label: '13:30' }, { t: 31, label: '15:30' }]}
  />
);

// ─── Grid ────────────────────────────────────────────────────────────────────
const gridChart = (
  <Chart
    minP={96.2} maxP={103.4}
    points={[[0, 100.4], [3, 99.6], [5, 98.8], [7, 98.2], [9, 99.2], [11, 100.3], [13, 101.3], [15, 101.9],
             [17, 101.1], [19, 100.2], [21, 99.3], [23, 98.1], [25, 96.8], [27, 98.0], [29, 99.1], [31, 100.4], [33, 101.2]]}
    levels={[
      { price: 103, label: 'Superior · 103', color: C.muted, solid: true },
      { price: 101, label: 'Línea 101', color: C.sell },
      { price: 99, label: 'Línea 99', color: C.target },
      { price: 97, label: 'Línea 97', color: C.buy },
    ]}
    marks={[
      { t: 4.5, price: 99, n: 1, color: C.buy, label: 'compra', dy: 20 },
      { t: 12.4, price: 101, n: 1, color: C.sell, label: 'vende', dy: -13 },
      { t: 21.6, price: 99, n: 2, color: C.buy, label: 'compra', dy: 20 },
      { t: 24.7, price: 97, n: 3, color: C.buy, label: 'compra', dy: 20 },
      { t: 28.8, price: 99, n: 3, color: C.sell, label: 'vende', dy: -13 },
      { t: 32, price: 101, n: 2, color: C.sell, label: 'vende', dy: -13 },
    ]}
    xLabels={[{ t: 0, label: 'lun' }, { t: 12, label: 'mar' }, { t: 24, label: 'mié' }, { t: 33, label: 'jue' }]}
  />
);

const STRATEGIES = [
  {
    key: 'dcascalps',
    name: 'DCA Scalps',
    tag: 'Compra caídas bruscas',
    tagColor: C.buy,
    summary: 'Espera un desplome fuerte y rápido, compra en 3 partes mientras sigue cayendo y vende todo apenas el precio rebota un poco sobre el precio promedio.',
    chart: dcaScalps,
    legend: [[C.buy, 'Compras'], [C.target, 'Objetivo de venta'], [C.price, 'Precio']],
    steps: [
      'Vigila el precio minuto a minuto, sin posición.',
      'Si en 15 minutos el precio cae 4% o más desde el máximo y el RSI de 15 min está en 30 o menos (mercado sobrevendido), compra la primera parte (1/3 del monto).',
      'Si sigue cayendo, compra la segunda parte a −3% de la primera compra y la tercera a −6%.',
      'Vende todo cuando el precio sube 1.5% sobre el precio promedio de las compras.',
      'Si no rebota, vende todo en el stop (−25% bajo la primera compra) o a las 24 horas, lo que pase primero.',
    ],
    rules: [
      ['Entrada', 'Caída ≥ 4% en 15 min con RSI (15 min) ≤ 30'],
      ['Compras', '3 partes iguales: en la señal, a −3% y a −6%'],
      ['Venta', '+1.5% sobre el precio promedio'],
      ['Stop', '−25% bajo la primera compra'],
      ['Tiempo máximo', '24 horas desde la primera compra'],
    ],
    example: [
      ['Cada compra', '50 USDT'],
      ['Si solo compra 1 vez', 'vende 50 USDT con +1.5% ≈ +0.6 USDT neto'],
      ['Si compra las 3', 'el promedio baja a 93.04 y vende en 94.44: ≈ +2 USDT neto'],
    ],
    history: 'Opera poco: solo cuando hay desplomes. En meses tranquilos puede pasar semanas sin operar.',
  },
  {
    key: 'dcadual',
    name: 'DCA Dual',
    tag: 'Caídas bruscas + mercado tranquilo',
    tagColor: C.ema,
    summary: 'Es el DCA Scalps con un segundo modo para cuando el mercado está tranquilo: compra caídas más chicas (2%) pero solo si la moneda viene en tendencia alcista de largo plazo. Usa el mismo monto y puede tener hasta dos operaciones abiertas a la vez, sin pasarse nunca del monto del bot.',
    chart: dualCalm,
    legend: [[C.buy, 'Compras'], [C.target, 'Objetivo de venta'], [C.ema, 'EMA de 200 días (tendencia)'], [C.price, 'Precio']],
    steps: [
      'Modo caída brusca: como DCA Scalps, pero con una caída mínima según la moneda (SOL 3%, ETH 3.5%, BTC 2.5%, SUI 3%, DOGE 3%) y RSI ≤ 30. Tiene prioridad.',
      'Modo volatilidad baja: si la caída es de 2% o más en 15 min, el RSI de 15 min está en 40 o menos y el cierre de ayer quedó sobre la EMA de 200 días, compra 1/3 del monto.',
      'En modo baja las compras siguientes son a −1.5% y −3% de la primera.',
      'Vende todo con +1.6% sobre el promedio; stop a −8% de la primera compra o cierre a las 24 horas.',
      'Si con una operación abierta llega otra señal (de cualquiera de los dos modos), abre una segunda con el monto que queda libre. Entre las dos nunca gastan más que el monto del bot: si una compra no entra, se achica o se saltea.',
    ],
    rules: [
      ['Modo caída brusca', 'SOL 3%, ETH 3.5%, BTC 2.5%, SUI 3%, DOGE 3% en 15 min, RSI ≤ 30 · 3 compras cada 3% · +1.5% · stop 25% · 24 h'],
      ['Modo volatilidad baja', '2% en 15 min, RSI ≤ 40, precio sobre la EMA de 200 días · 3 compras cada 1.5% · +1.6% · stop 8% · 24 h'],
      ['Monedas del modo baja', 'SOL y DOGE. En BTC, ETH y SUI opera solo el modo caída brusca'],
      ['Posiciones', 'Hasta dos a la vez, compartiendo el monto (partes de 1/3 cada una)'],
    ],
    example: [
      ['Cada compra', '50 USDT'],
      ['Modo baja, 1 compra', 'vende con +1.6% ≈ +0.7 USDT neto'],
      ['Modo baja, peor caso', '3 compras y stop: ≈ −10 USDT'],
      ['Dos operaciones', 'la 1ª con 2 compras (100) y la 2ª con 1 (50): no queda monto para más compras'],
    ],
    history: 'El filtro de la EMA de 200 días evita comprar caídas chicas cuando la moneda viene bajando hace meses: ahí las caídas de 2% suelen seguir de largo. La caída brusca se ajustó por moneda porque BTC se mueve menos que SOL: en las pruebas de 2022 a 2026 subió las operaciones (SOL 459 a 557, ETH 76 a 116, BTC 33 a 99) sin años negativos.',
    extra: [
      {
        title: '¿Por qué a veces el modo volatilidad baja está apagado?',
        text: [
          'El modo baja se apaga solo en dos casos. Mientras está apagado el bot no queda congelado: sigue vigilando y opera el modo caída brusca.',
          '1. La moneda no es SOL ni DOGE. En BTC, ETH y SUI el modo baja está desactivado siempre. En las pruebas de 2022 a 2026 restaba: en ETH el bot ganaba +120 con el modo baja contra +172 sin él (2023 quedaba en −18.6), y en BTC 2025 quedaba en −26.5. En esas monedas las caídas de 2% muchas veces seguían de largo hasta la 3ª compra y el stop, y esas pérdidas se comían lo ganado. En SUI 2024-2026 restaba −134. En SOL y DOGE pasó lo contrario: sumó ganancia todos los años. Por eso en la pantalla Plan de un bot de BTC, ETH o SUI aparece "desactivado en esta moneda".',
          '2. En SOL y DOGE, cuando el precio está bajo la EMA de 200 días. La EMA de 200 días es el precio promedio de los últimos ~7 meses. Si el cierre de ayer quedó debajo, la moneda viene en tendencia bajista y el modo baja se pausa ese día; se revisa de nuevo cada día a las 00:00 UTC. Sin este filtro el modo baja perdía en años bajistas como 2022.',
        ],
      },
    ],
  },
  {
    key: 'short',
    name: 'Short',
    tag: 'Vende subidas bruscas',
    tagColor: C.sell,
    summary: 'El espejo del DCA Scalps: cuando la moneda sube muy rápido en un mercado bajista, vende en corto (margen) esperando que el precio baje un poco, y recompra más barato.',
    chart: shortChart,
    legend: [[C.sell, 'Ventas en corto'], [C.target, 'Recompra'], [C.price, 'Precio']],
    steps: [
      'Solo opera si el mercado viene bajista: el cierre de ayer está bajo la EMA de 20 días.',
      'Si en las últimas 4 horas el precio sube 7% o más sobre el mínimo y el RSI de 15 min está en 60 o más, vende en corto 1/4 del monto.',
      'Si sigue subiendo, vende otra parte cada +3% sobre la primera venta (hasta 4 partes).',
      'Recompra todo cuando el precio baja 1% bajo el precio promedio de venta: esa diferencia es la ganancia.',
      'Si no baja, recompra en el stop (+40% sobre la primera venta) o a las 72 horas.',
    ],
    rules: [
      ['Condición', 'Cierre de ayer bajo la EMA de 20 días'],
      ['Entrada', 'Subida ≥ 7% en 4 h con RSI (15 min) ≥ 60'],
      ['Ventas', '4 partes iguales: en la señal y cada +3%'],
      ['Recompra', '−1% bajo el precio promedio de venta'],
      ['Stop / tiempo', '+40% sobre la primera venta · 72 horas'],
    ],
    example: [
      ['Cada venta', '37.50 USDT'],
      ['Ganancia típica', '+0.5 a +2 USDT por operación'],
      ['Costos', 'comisión de margen e interés por el préstamo'],
    ],
    history: 'Usa la cuenta de margen de Binance: la key necesita permiso de margen.',
  },
  {
    key: 'grid',
    name: 'Grid',
    tag: 'Compra y vende entre líneas',
    tagColor: C.target,
    summary: 'Como el Spot Grid de Binance: divide un rango de precios en líneas con el mismo % de distancia. Compra cada vez que el precio baja a una línea y vende esa compra en la línea de arriba. Gana con el ir y venir del precio, no con que suba o baje.',
    chart: gridChart,
    legend: [[C.buy, 'Compras'], [C.sell, 'Ventas'], [C.muted, 'Límites del rango'], [C.price, 'Precio']],
    steps: [
      'Por defecto el rango es automático: al levantar el bot va del precio −15% al precio +15%, con líneas cada 2% (15 grids). También puedes elegir a mano el precio inferior, el superior y cuántos grids quieres. Las líneas quedan separadas por el mismo % (geométrico).',
      'Al empezar, con el precio dentro del rango, compra la moneda de las líneas que quedan arriba del precio, para tener qué vender ahí.',
      'Cada vez que el precio cruza una línea hacia abajo, compra la misma cantidad de moneda (a mercado). Esa compra se vende cuando el precio sube a la línea siguiente.',
      'Cada vuelta (compra en una línea y venta en la de arriba) deja el paso entre líneas menos la comisión de las dos órdenes (0.2% sin BNB).',
      'Si el precio sale del rango no opera: abajo queda todo en moneda, arriba todo en USDT. Vuelve a operar si el precio entra de nuevo.',
      'Al detener el bot vende toda la moneda del grid a precio de mercado.',
    ],
    rules: [
      ['Rango', 'automático: precio ±15% al levantar el bot, líneas cada 2% · o a mano: precio inferior y superior'],
      ['Líneas', 'geométricas: el mismo % entre una y la siguiente'],
      ['Cada línea', 'la misma cantidad de moneda; la más baja tiene que comprar al menos 5.5 USDT'],
      ['Paso mínimo', '0.25% entre líneas: con menos la comisión se come la ganancia'],
      ['Órdenes', 'a mercado cuando el precio cruza la línea'],
      ['Sin stop', 'si el precio cae bajo el rango, el bot queda con la moneda'],
    ],
    example: [
      ['Grid automático', 'BTC en 83 000, 205 USDT: rango 70 550 – 95 450, 15 grids, paso 2%'],
      ['Cada línea', '≈ 14 USDT'],
      ['Cada vuelta', '≈ +0.26 USDT (2% menos 0.2% de comisión)'],
      ['Si BTC baja de 70 550', 'queda todo en BTC: ≈ −11% del monto en ese precio, y más si sigue bajando'],
    ],
    history: 'Probado con velas de 1 minuto de 2024 a octubre de 2026 (205 USDT, comisión 0.1%, grid creado al principio de cada año): el automático ±15% con líneas cada 2% ganó en BTC +17.4, +43.2 y +5.4 USDT y en SOL +62.1, +30.0 y −9.4. Un grid más angosto como 75 000 – 85 000 con 33 grids ganó bastante menos (BTC +26.7 y SOL +4.3 en total): sale del rango más seguido y la comisión pesa más en cada vuelta. Rinde en mercados laterales y pierde menos que tener la moneda en los bajistas; en los alcistas sale del rango por arriba y deja de ganar.',
  },
];

const Estrategias = () => {
  const [open, setOpen] = useState('dcascalps');
  const current = STRATEGIES.find(s => s.key === open);
  return (
    <div style={{ marginTop: 20, maxWidth: 1100 }}>
      <div style={card}>
        <h2 style={{ marginTop: 0, color: C.price }}>Cómo funciona cada estrategia</h2>
        <p style={{ color: '#525f7f', fontSize: 15, marginBottom: 14 }}>
          Ninguna estrategia adivina el futuro: compran (o venden) cuando el precio se movió fuerte en poco tiempo,
          porque después de esos movimientos suele haber un rebote corto. Ganan poco por operación y cortan con un stop o
          por tiempo cuando el rebote no llega.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {STRATEGIES.map(s => (
            <button key={s.key} onClick={() => setOpen(s.key)}
              style={{
                border: `2px solid ${s.tagColor}`, borderRadius: 10, padding: '8px 16px', fontWeight: 700, fontSize: 14,
                background: open === s.key ? s.tagColor : 'white', color: open === s.key ? 'white' : s.tagColor, cursor: 'pointer',
              }}>
              {s.name}
            </button>
          ))}
        </div>
      </div>

      <Section {...current} />

      <div style={card}>
        <div style={title}>Reglas de todos los bots</div>
        <Rules rows={[
          ['Un bot por moneda', 'Cada usuario puede tener un solo bot por moneda (BTC, ETH, SOL, SUI, DOGE).'],
          ['Monto mínimo', '15 USDT por bot. El monto se divide en partes iguales entre las compras (o ventas).'],
          ['Límite diario', 'Si en el día pierde 5% del monto, no abre operaciones nuevas hasta el día siguiente.'],
          ['RSI', 'Indicador de 0 a 100 calculado con velas de 15 minutos: bajo = muy vendido (posible rebote), alto = muy comprado.'],
          ['Detener', 'Al detener un bot se vende (o recompra) lo que tenga abierto.'],
          ['Comisión', '5% de la ganancia neta del mes. Si queda vencida, los bots no abren operaciones nuevas.'],
        ]} />
        <Note>Los gráficos son ejemplos para explicar las reglas, no operaciones reales. Los resultados pasados no garantizan resultados futuros.</Note>
      </div>
    </div>
  );
};

export default Estrategias;
